import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import FetchPosts from "../Helpers/fetchPosts";
import UserInfo from "./UserInfo";
import { useGlobalState } from "../contexts/GlobalStateContext";

const Profile = () => {
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const {
    globalState,
    setPageName,
    resetGlobalState,
    socketRef,
    setAuth,
    setUser,
    setGlobalState,
  } = useGlobalState();
  const location = useLocation();

  // Extract username from URL
  const usernameMatch = location.pathname.match(/^\/@([^/]+)$/);
  const cleanUsername = usernameMatch ? usernameMatch[1] : "";

  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // Fetch profile
  useEffect(() => {
    if (!cleanUsername) return;

    const fetchProfile = async () => {
      setLoading(true);
      setError(false);
      try {
        const res = await fetch(`${baseUrl}/php/get_profile.php?user=${encodeURIComponent(cleanUsername)}`);
        const data = await res.json();
        setProfileData(data);

        if (!data.user_id) {
          console.error("User ID missing:", data);
          setError(true);
        }
      } catch (e) {
        console.error("Profile fetch error:", e);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [cleanUsername, globalState.viewAsPublic, globalState.isAuthenticated]);

  // Set default tab to "posts" when username changes
  useEffect(() => {
    setPageName("posts");
  }, [cleanUsername]);

  // Logout helper
  const logOut = () => {
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.close(1000, "User logged out");
    }
    socketRef.current = null;

    fetch("/php/logout.php", { method: "GET", credentials: "include" }).catch(console.error);

    setUser(null);
    setAuth(false);
  };

  // Handle tab click and refresh FetchPosts
  const handleTabClick = (tabName) => {
    setPageName(tabName);
    setGlobalState((prev) => ({
      ...prev,
      refreshKeys: {
        ...prev.refreshKeys,
        [tabName]: (prev.refreshKeys?.[tabName] || 0) + 1,
      },
    }));
  };

  // If logged in user profile fails
  if (globalState?.user?.username === cleanUsername && error) {
    resetGlobalState();
    logOut();
    return null;
  }

  if (error) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "50vh" }}>
        Error loading profile
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "50vh" }}>
        Loading...
      </div>
    );
  }

  // Define all possible tabs
  const tabs = [
    { name: "escalated", label: "E", condition: globalState.isAuthenticated && profileData.self && profileData.user_status > 2 && !globalState.viewAsPublic },
    { name: "reports", label: "REPS", condition: globalState.isAuthenticated && profileData.self && profileData.user_status > 1 && !globalState.viewAsPublic },
    { name: "featured", label: "FEATURED", condition: true },
    { name: "posts", label: "MEMBERS", condition: true },
    { name: "liked", label: "LIKED", condition: true },
    { name: "drafts", label: "DRAFTS", condition: globalState.isAuthenticated && profileData.self && !globalState.viewAsPublic },
    { name: "saved", label: "SAVED", condition: globalState.isAuthenticated && profileData.self && !globalState.viewAsPublic },
    { name: "private", label: "PRIVATE", condition: globalState.isAuthenticated && profileData.self && !globalState.viewAsPublic },
  ];

  return (
    <div id="profile">
      <div className="row">
        {/* Column 1: always visible */}
        <div id="column1">
          <UserInfo data={profileData} />
        </div>

        {/* Column 2: posts and tabs */}
        <div id="column2">
            <div className="postsMenu">
              {tabs.filter(tab => tab.condition).map(tab => (
                <button
                  key={tab.name}
                  className="posts_menu"
                  style={{
                    borderBottom: globalState.pageName === tab.name ? "3px solid #3DDC84" : "none",
                  }}
                  onClick={() => handleTabClick(tab.name)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

          <div className="profile_posts">
            <FetchPosts
              key={`${cleanUsername}-${globalState.pageName}-${globalState.refreshKeys?.[globalState.pageName] || 0}`}
              username={cleanUsername}
              page={globalState.pageName}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
